import { z } from "zod";
import {
  BOT_LEVELS,
  CHAT_MAX_LENGTH,
  EMOJI_REACTIONS,
  NAME_MAX_LENGTH,
  PLAYER_COLOR_IDS,
  ROOM_CODE_ALPHABET,
  ROOM_CODE_LENGTH,
  TOKENS,
} from "./constants";
import type { GameEvent, GameOptions, PlayerId, PublicGameState } from "./types";
import type { BotLevel, PlayerColorId, TokenId } from "./constants";

// ---------------------------------------------------------------- game actions

const field = z.number().int().min(0).max(39);
const version = z.number().int().min(0).optional();

const tradeSide = z.object({
  cash: z.number().int().min(0).max(1_000_000),
  fields: z.array(field).max(28),
  jailCards: z.array(z.string().max(16)).max(4),
});

/** Intents a player may send. The server adds the acting player from the session. */
export const playerActionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("ROLL_DICE"), expectedVersion: version }),
  z.object({ type: z.literal("BUY_PROPERTY"), expectedVersion: version }),
  z.object({ type: z.literal("DECLINE_PURCHASE"), expectedVersion: version }),
  z.object({ type: z.literal("BID"), amount: z.number().int().min(1).max(1_000_000), expectedVersion: version }),
  z.object({ type: z.literal("PASS_AUCTION"), expectedVersion: version }),
  z.object({ type: z.literal("PAY_JAIL_FINE"), expectedVersion: version }),
  z.object({ type: z.literal("USE_JAIL_CARD"), expectedVersion: version }),
  z.object({ type: z.literal("CHOOSE_CARD_OPTION"), choice: z.enum(["PAY", "DRAW"]), expectedVersion: version }),
  z.object({ type: z.literal("END_TURN"), expectedVersion: version }),
  z.object({ type: z.literal("CLAIM_RENT"), expectedVersion: version }),
  z.object({ type: z.literal("BUILD"), field, expectedVersion: version }),
  z.object({ type: z.literal("SELL_BUILDING"), field, expectedVersion: version }),
  z.object({ type: z.literal("MORTGAGE"), field, expectedVersion: version }),
  z.object({ type: z.literal("UNMORTGAGE"), field, expectedVersion: version }),
  z.object({
    type: z.literal("MORTGAGE_DECISION"),
    field,
    choice: z.enum(["UNMORTGAGE", "KEEP"]),
    expectedVersion: version,
  }),
  z.object({ type: z.literal("PROPOSE_TRADE"), to: z.string().max(64), give: tradeSide, get: tradeSide, expectedVersion: version }),
  z.object({ type: z.literal("ACCEPT_TRADE"), tradeId: z.number().int(), expectedVersion: version }),
  z.object({ type: z.literal("REJECT_TRADE"), tradeId: z.number().int(), expectedVersion: version }),
  z.object({ type: z.literal("CANCEL_TRADE"), tradeId: z.number().int(), expectedVersion: version }),
  z.object({
    type: z.literal("COUNTER_TRADE"),
    tradeId: z.number().int(),
    give: tradeSide,
    get: tradeSide,
    expectedVersion: version,
  }),
  z.object({ type: z.literal("DECLARE_BANKRUPTCY"), expectedVersion: version }),
  z.object({ type: z.literal("RESIGN"), expectedVersion: version }),
]);
export type PlayerAction = z.infer<typeof playerActionSchema>;
export type PlayerActionType = PlayerAction["type"];

/** Actions only the server may issue (timers). */
export type SystemAction =
  | { type: "SYSTEM_CLOSE_AUCTION"; auctionId: number; activity: number; expectedVersion?: number }
  | { type: "SYSTEM_CLOSE_BUILD_WINDOW"; expectedVersion?: number }
  | { type: "SYSTEM_TIME_LIMIT"; expectedVersion?: number };

/** What the engine consumes: an action plus the acting player (`"system"` for timer actions). */
export type EngineAction = (PlayerAction & { player: PlayerId }) | (SystemAction & { player: "system" });

// ---------------------------------------------------------------- lobby

const nameSchema = z
  .string()
  .trim()
  .min(1)
  .max(NAME_MAX_LENGTH);

export const roomCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .length(ROOM_CODE_LENGTH)
  .refine((s) => [...s].every((c) => ROOM_CODE_ALPHABET.includes(c)), "invalid code");

export const optionsSchema = z.object({
  rentMode: z.enum(["AUTO", "CLAIM"]),
  turnTimerSeconds: z.union([z.literal(0), z.literal(30), z.literal(60), z.literal(90)]),
  startingCash: z.number().int().min(100).max(100_000),
  timeLimitMinutes: z.number().int().min(0).max(600),
  houseRules: z.object({
    freeParkingPot: z.boolean(),
    doubleGoOnLanding: z.boolean(),
    noAuction: z.boolean(),
    buildAfterAllStreetsSold: z.boolean(),
    dealStreets: z.boolean(),
  }),
});

/** Client -> server socket events. Every event has an ack callback returning `Ack`. */
export const clientMessages = {
  "room:join": z.object({ code: roomCodeSchema, name: nameSchema }),
  "room:leave": z.object({}),
  "lobby:update": z.object({
    token: z.enum(TOKENS).optional(),
    color: z.enum(PLAYER_COLOR_IDS as [PlayerColorId, ...PlayerColorId[]]).optional(),
    ready: z.boolean().optional(),
    name: nameSchema.optional(),
  }),
  "lobby:options": optionsSchema,
  "lobby:kick": z.object({ playerId: z.string().max(64) }),
  "lobby:addBot": z.object({ level: z.enum(BOT_LEVELS).optional() }),
  "lobby:botLevel": z.object({ playerId: z.string().max(64), level: z.enum(BOT_LEVELS) }),
  "lobby:removeBot": z.object({ playerId: z.string().max(64) }),
  "lobby:start": z.object({}),
  "game:action": z.object({ action: playerActionSchema }),
  "chat:send": z.object({ text: z.string().trim().min(1).max(CHAT_MAX_LENGTH) }),
  "chat:reaction": z.object({ emoji: z.enum(EMOJI_REACTIONS) }),
} as const;

export type ClientMessageName = keyof typeof clientMessages;
export type ClientMessagePayload<K extends ClientMessageName> = z.infer<(typeof clientMessages)[K]>;

export type Ack<T = unknown> = { ok: true; data?: T } | { ok: false; code: string; message: string };

// ---------------------------------------------------------------- server -> client

export interface RoomMember {
  id: PlayerId;
  name: string;
  token: TokenId | null;
  color: PlayerColorId | null;
  ready: boolean;
  isBot: boolean;
  /** Strength of a bot, null for people. */
  botLevel: BotLevel | null;
  isHost: boolean;
  connected: boolean;
  /** Server took over for this player (disconnect timeout). */
  autoPlay: boolean;
}

export interface ChatMessage {
  id: number;
  at: number;
  playerId: PlayerId | null;
  name: string;
  text: string;
  kind: "chat" | "system";
}

export interface RoomTimers {
  /** Epoch ms of the server clock when the view was built. Clients correct for clock skew with it. */
  serverNow: number;
  auctionEndsAt: number | null;
  buildWindowEndsAt: number | null;
  turnEndsAt: number | null;
  /** Player whose disconnect countdown runs, and when the server takes over. */
  autoPlayAt: { playerId: PlayerId; at: number } | null;
  gameEndsAt: number | null;
}

export interface RoomView {
  code: string;
  status: "LOBBY" | "PLAYING" | "FINISHED";
  /** The viewer. `playerId` is null for spectators. */
  you: { playerId: PlayerId | null; isHost: boolean; spectator: boolean };
  members: RoomMember[];
  spectators: number;
  options: GameOptions;
  game: PublicGameState | null;
  timers: RoomTimers;
  chat: ChatMessage[];
}

export interface ServerToClientEvents {
  "room:state": (view: RoomView) => void;
  /** Events produced by the last action, for log entries, animation and sound. */
  "game:events": (payload: { version: number; events: GameEvent[] }) => void;
  "chat:message": (msg: ChatMessage) => void;
  "chat:reaction": (payload: { playerId: PlayerId; emoji: string }) => void;
  "room:kicked": (payload: { reason: string }) => void;
  error: (payload: { code: string; message: string }) => void;
}

export type ErrorCode =
  | "BAD_REQUEST"
  | "RATE_LIMITED"
  | "ROOM_NOT_FOUND"
  | "ROOM_FULL"
  | "NOT_HOST"
  | "NOT_IN_ROOM"
  | "GAME_RUNNING"
  | "NOT_ENOUGH_PLAYERS"
  | "NOT_READY"
  | "TOKEN_TAKEN"
  | "COLOR_TAKEN"
  | "VERSION_CONFLICT"
  | "ENGINE";

// ---------------------------------------------------------------- HTTP

export const createRoomBody = z.object({ name: nameSchema });
export type CreateRoomBody = z.infer<typeof createRoomBody>;
export interface CreateRoomResponse {
  code: string;
}

/** GET /api/rooms/:code */
export interface RoomInfoResponse {
  exists: boolean;
  status?: "LOBBY" | "PLAYING" | "FINISHED";
  players?: number;
}
